/**
 * @file Dashboard.js
 * @description View principal que exibe as estatísticas e classificações.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Users, Shield, BarChart2, FileDown, Star, ChevronDown, Trophy } from "lucide-react";
import TabelaClassificacaoGeral from "../components/TabelaGeral";
import EscalacaoModal from "../components/EscalacaoModal";
import { getEscudoUrl } from "../api/ligaApi";
import { RODADA_INICIAL, RODADA_FINAL, RODADAS_DO_TORNEIO } from "../config";

// Mobile-first: vira um chip compacto e centralizado em telas pequenas,
// expandindo para o layout horizontal com ícone a partir de sm
const StatCard = ({ icon, title, value, accentColor }) => (
  <div className="bg-campo-900 border border-campo-700/50 rounded-xl p-2.5 sm:p-5 flex flex-col sm:flex-row items-center gap-1 sm:gap-4 relative overflow-hidden shadow-md text-center sm:text-left">
    <div className={`absolute left-0 inset-y-0 w-1 rounded-l-xl ${accentColor}`} />
    <div className="hidden sm:block p-3 rounded-xl bg-white/5 flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-2xl sm:text-3xl font-black text-white tabular-nums">{value}</p>
      <p className="text-[10px] sm:text-sm text-gray-400 font-medium leading-tight">{title}</p>
    </div>
  </div>
);

// Indicador de pontuação parcial ao vivo
const BadgeAoVivo = ({ label = "Parciais ao vivo" }) => (
  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-red-400 uppercase tracking-wider">
    <span className="relative flex h-1.5 w-1.5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
    </span>
    {label}
  </span>
);

const Dashboard = ({ ligaData, parciais, handleGerarPDF, handleGerarPDFGeral, isAdmin }) => {
  const { equipes, rodadas } = ligaData;
  // null até o efeito definir a rodada sugerida; rodadaAtiva sempre tem valor
  const [selectedRodada, setSelectedRodada] = useState(null);
  // Não sobrescreve a rodada depois que o usuário escolher uma manualmente
  const escolhaManualRef = useRef(false);
  // Cards da rodada recolhidos por padrão; permite expandir vários ao mesmo tempo
  const [expandedEquipes, setExpandedEquipes] = useState(() => new Set());
  // Time selecionado para ver a escalação no Cartola (Detalhes da Rodada)
  const [escalacaoTime, setEscalacaoTime] = useState(null);

  const toggleEquipeExpand = (equipeId) => {
    setExpandedEquipes((prev) => {
      const next = new Set(prev);
      if (next.has(equipeId)) {
        next.delete(equipeId);
      } else {
        next.add(equipeId);
      }
      return next;
    });
  };

  // Torneio de returno: vale da rodada 20 à 38 (ver src/config.js). Vale para
  // todos — público e admin veem as mesmas rodadas do torneio.
  const rodadasDisponiveis = RODADAS_DO_TORNEIO;

  const rodadasComPontos = rodadas.filter((r) => r.pontos != null);
  const totalRodadasFinalizadas = [
    ...new Set(rodadasComPontos.map((r) => r.numero)),
  ].length;

  // Parciais valem enquanto a rodada ao vivo ainda não tem pontuação oficial no banco
  const parciaisAtivas = useMemo(
    () =>
      !!parciais?.ativo &&
      !rodadas.some((r) => r.numero === parciais.rodada && r.pontos != null)
        ? { rodada: parciais.rodada, valores: parciais.parciais || {} }
        : null,
    [parciais, rodadas],
  );

  // Rodada a abrir por padrão: a que está ao vivo, senão a última com
  // pontuação no banco, senão a primeira rodada do torneio (20)
  const rodadaSugerida = useMemo(() => {
    if (parciaisAtivas) return parciaisAtivas.rodada;
    const comPontos = rodadas.filter((r) => r.pontos != null).map((r) => r.numero);
    return comPontos.length ? Math.max(...comPontos) : RODADA_INICIAL;
  }, [parciaisAtivas, rodadas]);

  // Sincroniza com a sugestão enquanto o usuário não escolher manualmente
  useEffect(() => {
    if (!escolhaManualRef.current) setSelectedRodada(rodadaSugerida);
  }, [rodadaSugerida]);

  const rodadaAtiva = selectedRodada ?? rodadaSugerida;

  const handleRodadaChange = (numero) => {
    escolhaManualRef.current = true;
    setSelectedRodada(numero);
  };

  const getPontosPorTimeNaRodada = useCallback(
    (timeId, rodadaNum) => {
      if (parciaisAtivas && rodadaNum === parciaisAtivas.rodada) {
        return parciaisAtivas.valores[timeId.toString()] ?? 0;
      }
      const ponto = rodadas.find(
        (r) =>
          r.numero === rodadaNum &&
          r.time_id &&
          r.time_id.toString() === timeId.toString(),
      );
      return ponto ? ponto.pontos : 0;
    },
    [rodadas, parciaisAtivas],
  );

  const equipesPorRodada = useMemo(() => {
    const getPontosPorEquipeNaRodada = (equipe, rodadaNum) =>
      equipe.times.reduce(
        (total, time) => total + getPontosPorTimeNaRodada(time.id, rodadaNum),
        0,
      );
    return [...equipes]
      .map((equipe) => ({
        ...equipe,
        pontosNaRodada: getPontosPorEquipeNaRodada(equipe, rodadaAtiva),
      }))
      .sort((a, b) => b.pontosNaRodada - a.pontosNaRodada);
  }, [equipes, rodadaAtiva, getPontosPorTimeNaRodada]);

  const rodadaAoVivo = !!parciaisAtivas && rodadaAtiva === parciaisAtivas.rodada;
  const rodadaTemDados =
    equipesPorRodada.some((e) => e.pontosNaRodada > 0) || rodadaAoVivo;

  return (
    <div className="space-y-6">
      {/* Hero do torneio */}
      <section className="relative overflow-hidden rounded-2xl border border-campo-700/50 bg-gradient-to-br from-campo-800 via-campo-900 to-campo-950 shadow-lg">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-ouro to-transparent" />
        {/* Troféu ao fundo */}
        <Trophy
          strokeWidth={1.25}
          className="absolute -right-6 -bottom-10 w-44 h-44 sm:w-60 sm:h-60 text-ouro/15 rotate-12 pointer-events-none"
        />
        <div className="relative p-5 sm:p-8">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-ouro mb-2.5">
            Cartola FC • Brasileirão 2026
          </p>
          <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-white leading-tight">
            Torneio de Equipes
          </h1>
          <p className="mt-2 text-xs sm:text-sm text-gray-400">
            Rodadas {RODADA_INICIAL} a {RODADA_FINAL} • pontos corridos •{" "}
            {equipes.length} equipes na disputa
          </p>
        </div>
      </section>

      {/* Cards de estatísticas */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <StatCard
          icon={<Shield size={20} className="text-emerald-400" />}
          title="Equipes na Disputa"
          value={equipes.length}
          accentColor="bg-emerald-500"
        />
        <StatCard
          icon={<Users size={20} className="text-ouro" />}
          title="Total de Times"
          value={equipes.reduce((acc, equipe) => acc + equipe.times.length, 0)}
          accentColor="bg-ouro"
        />
        <StatCard
          icon={<BarChart2 size={20} className="text-sky-400" />}
          title="Rodadas Finalizadas"
          value={totalRodadasFinalizadas}
          accentColor="bg-sky-500"
        />
      </div>

      {/* Classificação Geral */}
      <div className="bg-campo-900 border border-campo-700/50 rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-campo-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-base sm:text-lg font-bold text-white">
              <Trophy size={17} className="text-ouro flex-shrink-0" />
              Classificação Geral
              {parciaisAtivas && <BadgeAoVivo label="Com parciais" />}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {parciaisAtivas
                ? `Pontuação acumulada + parcial da rodada ${parciaisAtivas.rodada}`
                : "Pontuação acumulada de todas as rodadas"}
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={handleGerarPDFGeral}
              className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
            >
              <FileDown size={15} />
              Exportar PDF
            </button>
          )}
        </div>
        <div className="p-4 sm:p-5">
          <TabelaClassificacaoGeral
            equipes={equipes}
            rodadasData={rodadas}
            parciais={parciaisAtivas}
          />
        </div>
      </div>

      {/* Detalhes por Rodada */}
      <div className="bg-campo-900 border border-campo-700/50 rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-campo-700/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2.5 text-base sm:text-lg font-bold text-white">
                Detalhes da Rodada
                {rodadaAoVivo && <BadgeAoVivo />}
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {rodadaAoVivo
                  ? "Pontuação parcial da rodada em andamento"
                  : "Pontuação por equipe em cada rodada"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Select customizado */}
              <div className="relative flex-1 sm:flex-none">
                <select
                  value={rodadaAtiva}
                  onChange={(e) => handleRodadaChange(Number(e.target.value))}
                  className="w-full sm:w-auto appearance-none bg-campo-800 border border-campo-600 text-white py-2 pl-3 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ouro cursor-pointer"
                >
                  {rodadasDisponiveis.map((rodadaNum) => (
                    <option key={rodadaNum} value={rodadaNum}>
                      Rodada {rodadaNum}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleGerarPDF(rodadaAtiva)}
                  className="flex items-center gap-2 bg-ouro hover:bg-ouro-light text-campo-950 font-bold py-2 px-3 sm:px-4 rounded-lg transition-colors text-sm flex-shrink-0"
                >
                  <FileDown size={15} />
                  <span className="hidden sm:inline">PDF</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {/* Empty state quando não há dados na rodada */}
          {!rodadaTemDados ? (
            <div className="text-center py-10">
              <BarChart2 size={36} className="text-campo-700 mx-auto mb-3" />
              <p className="text-gray-400 font-medium text-sm">Sem dados para esta rodada</p>
              <p className="text-gray-600 text-xs mt-1">
                As pontuações ainda não foram sincronizadas.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {equipesPorRodada.map((equipe, index) => {
                const isExpanded = expandedEquipes.has(equipe.id);
                return (
                <div
                  key={equipe.id}
                  className="bg-campo-950/60 border border-campo-700/50 rounded-xl overflow-hidden"
                >
                  {/* Cabeçalho da equipe (clicável para expandir os times) */}
                  <div
                    role="button"
                    aria-expanded={isExpanded}
                    onClick={() => toggleEquipeExpand(equipe.id)}
                    className={`flex items-center justify-between px-3 sm:px-4 py-3 cursor-pointer select-none hover:bg-white/5 active:bg-white/10 transition-colors ${
                      isExpanded ? "border-b border-campo-700/30" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className="text-sm font-bold text-gray-500 w-4 text-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <img
                        src={getEscudoUrl(equipe.logo_url) || "/logo-equipe-padrao.png"}
                        alt=""
                        loading="lazy"
                        className="w-9 h-9 rounded-full object-contain flex-shrink-0"
                      />
                      <div className="flex items-center gap-1.5 min-w-0">
                        <h4 className="font-bold text-white text-sm sm:text-base truncate">
                          {equipe.nome}
                        </h4>
                        {equipe.titulos > 0 && (
                          <div
                            className="relative flex items-center justify-center w-5 h-5 flex-shrink-0"
                            title={`${equipe.titulos} Títulos`}
                          >
                            <Star size={20} className="text-amber-400 fill-amber-400" />
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-campo-950">
                              {equipe.titulos}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center flex-shrink-0 ml-2">
                      <span className="text-xl sm:text-2xl font-black text-white tabular-nums">
                        {equipe.pontosNaRodada.toFixed(2)}
                      </span>
                      <span className="text-gray-500 text-xs ml-1">pts</span>
                      <ChevronDown
                        size={15}
                        className={`text-gray-400 ml-1.5 transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </div>

                  {/* Lista de times */}
                  {isExpanded && (
                    <div className="divide-y divide-campo-700/30">
                      {[...equipe.times]
                        .sort((a, b) => {
                          const pontosA = getPontosPorTimeNaRodada(a.id, selectedRodada);
                          const pontosB = getPontosPorTimeNaRodada(b.id, selectedRodada);
                          if (pontosB !== pontosA) return pontosB - pontosA;
                          return a.nome_time.localeCompare(b.nome_time);
                        })
                        .map((time) => (
                          <div
                            key={time.id}
                            role="button"
                            title="Ver escalação no Cartola"
                            onClick={() => setEscalacaoTime(time)}
                            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 cursor-pointer select-none hover:bg-white/5 active:bg-white/10 transition-colors"
                          >
                            <img
                              src={getEscudoUrl(time.escudo_url)}
                              alt={`Escudo do ${time.nome_time}`}
                              loading="lazy"
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-white text-sm truncate flex items-center gap-1.5">
                                <span className="truncate">{time.nome_time}</span>
                                {time.is_captain && (
                                  <span className="text-ouro font-bold text-xs flex-shrink-0">
                                    (C)
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                {time.nome_cartoleiro}
                              </p>
                            </div>
                            <span className="font-bold text-ouro text-sm tabular-nums flex-shrink-0">
                              {getPontosPorTimeNaRodada(time.id, selectedRodada).toFixed(2)}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
        </div>
      </div>

      {escalacaoTime && (
        <EscalacaoModal
          time={escalacaoTime}
          rodada={rodadaAtiva}
          onClose={() => setEscalacaoTime(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
